import { SECRET_KEY } from '@aca-new/app/shared/constants/secret-key.constants';
import { ECookieKeys } from '@aca-new/app/shared/models/enums/cookie-keys.enum';
import { Injectable } from '@angular/core';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as CryptoJS from 'crypto-js';
import { CookieService } from 'ngx-cookie';

export interface ICookieOptions {
  domain?: string;
  encrypt?: boolean;
  expires?: Date;
  path?: string;
  sameSite?: 'lax' | 'strict' | 'none';
  secure?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CookieStorageService {
  private readonly _defaultOptions: ICookieOptions = {
    secure: true,
    sameSite: 'lax',
    encrypt: true, // Default encryption enabled to protect sensitive data
  };

  public constructor(private readonly _cookieService: CookieService) {}

  /**
   * Get a cookie value by key
   * @param {ECookieKeys} key - The cookie key
   * @returns {string} The decrypted cookie value if encryption was used, otherwise the raw value
   */
  public get(key: ECookieKeys): string {
    const value = this._cookieService.get(key) ?? '';

    try {
      // Extract IV (first 32 hex chars = 16 bytes) and ciphertext
      const ivHex = value.slice(0, 32);
      const encryptedHex = value.slice(32);

      if (!ivHex || !encryptedHex) {
        return value;
      }

      const iv = CryptoJS.enc.Hex.parse(ivHex);
      const decrypted = CryptoJS.AES.decrypt(encryptedHex, SECRET_KEY, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });

      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      // If decryption fails, return the original value (not encrypted)
      return value;
    }
  }

  /**
   * Set a cookie value with default options
   * @param {ECookieKeys} key - The cookie key
   * @param {string} value - The cookie value
   * @param {ICookieOptions} options - Optional cookie options to override defaults
   */
  public set(key: ECookieKeys, value: string, options?: ICookieOptions): void {
    const finalOptions = { ...this._defaultOptions, ...options };
    const { encrypt: shouldEncrypt, ...cookieOptions } = finalOptions;

    if (shouldEncrypt) {
      const iv = this._generateIV();
      const encrypted = CryptoJS.AES.encrypt(value, SECRET_KEY, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      const encryptedValue: string = this._combineIVAndCiphertext(iv, encrypted);

      this._cookieService.put(key, encryptedValue, cookieOptions);
    } else {
      this._cookieService.put(key, value, cookieOptions);
    }
  }

  /**
   * Remove a cookie by key
   * @param {ECookieKeys} key - The cookie key
   */
  public remove(key: ECookieKeys): void {
    this._cookieService.remove(key);
  }

  /**
   * Remove all cookies
   */
  public removeAll(): void {
    this._cookieService.removeAll();
  }

  /**
   * Generate a random initialization vector (IV)
   * @returns {CryptoJS.lib.WordArray} The generated IV
   */
  private _generateIV(): CryptoJS.lib.WordArray {
    return CryptoJS.lib.WordArray.random(16); // 16 bytes = 128 bits
  }

  /**
   * Combine IV and ciphertext into a single string
   * @param {CryptoJS.lib.WordArray} iv - The initialization vector
   * @param {CryptoJS.lib.CipherParams} encrypted - The encrypted data
   * @returns {string} The combined string (IV in hex + ciphertext in base64)
   */
  private _combineIVAndCiphertext(iv: CryptoJS.lib.WordArray, encrypted: CryptoJS.lib.CipherParams): string {
    const ivHex = iv.toString(CryptoJS.enc.Hex);
    const ciphertextBase64 = encrypted.toString();

    return ivHex + ciphertextBase64;
  }
}
